import { ConfirmService, HBPricerResponse, isPricerError, PBPricerResponse, PricerErrorResponse } from '@mmx/shared'
import { Observable } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'

/**
 * Minimal shape of a pricer charge-grouping key.
 *
 * `appointmentId` is optional and may be `null` because the estimate output
 * visits use `null` for unscheduled visits while pricer keys omit it entirely.
 */
export interface ChargeGroupingLike {
  visitNumber: number
  appointmentId?: string | null
}

/**
 * Determines whether a stored pricer `key` corresponds to the requested visit
 * `grouping`.
 *
 * Pricer request/response maps are keyed by visit number only — their keys
 * carry no `appointmentId` — whereas the visit grouping passed in does once
 * appointments are linked (e.g. after finalize). The `appointmentId` is
 * therefore only compared when the stored key actually has one; otherwise the
 * trace would be considered missing for scheduled estimates.
 *
 * @param key Stored pricer charge grouping (keyed by visit number).
 * @param grouping Visit grouping the trace is requested for.
 */
export function chargeGroupingMatches(key: ChargeGroupingLike, grouping: ChargeGroupingLike): boolean {
  if (key.visitNumber !== grouping.visitNumber) {
    return false
  }

  return key.appointmentId == null || key.appointmentId === grouping.appointmentId
}

/**
 * Finds the value in a pricer entries collection (array of `[key, value]`
 * tuples or a `Map`) whose key matches the requested visit `grouping`.
 *
 * @param entries Pricer request/response entries, or `undefined`.
 * @param grouping Visit grouping the trace is requested for.
 * @returns The matching value, or `undefined` when none matches.
 */
export function findPricerTraceEntry<V>(
  entries: Iterable<readonly [ChargeGroupingLike, V]> | undefined,
  grouping: ChargeGroupingLike,
): V | undefined {
  if (!entries) {
    return undefined
  }

  for (const [key, value] of entries) {
    if (chargeGroupingMatches(key, grouping)) {
      return value
    }
  }

  return undefined
}

/**
 * Whether a PB/HB pricer trace exists for the given visit `grouping` — true
 * when either a response or a request entry matches with a non-nullish value.
 *
 * The nullish (`!= null`) check is intentional so it stays aligned with
 * `buildPricerTraceMessage`, which treats a missing/falsy response or request
 * as "nothing to show". This keeps the guarantee that a visible trace control
 * always has something to open.
 *
 * @param responses Pricer response entries, or `undefined`.
 * @param requests Pricer request entries, or `undefined`.
 * @param grouping Visit grouping the trace is requested for.
 */
export function hasPricerTrace(
  responses: Iterable<readonly [ChargeGroupingLike, unknown]> | undefined,
  requests: Iterable<readonly [ChargeGroupingLike, unknown]> | undefined,
  grouping: ChargeGroupingLike,
): boolean {
  return findPricerTraceEntry(responses, grouping) != null
    || findPricerTraceEntry(requests, grouping) != null
}

/**
 * Builds the HTML message string for the pricer-trace confirm dialog.
 *
 * Returns `null` only when there is neither a response nor a request to show
 * (caller should skip opening the dialog). When a request exists but the
 * response is missing — which can happen because the trace control is shown
 * whenever a response OR request entry exists — the raw request is shown with a
 * "no response" note so the dialog still opens.
 *
 * @param kind     - 'pb' for professional-billing or 'hb' for hospital-billing
 * @param response - pricer response value (may be undefined)
 * @param request  - pricer request value (may be unknown)
 * @param t        - translation helper already scoped to the
 *                   `patient-details.estimate-card.advanced.*` namespace
 */
export function buildPricerTraceMessage(
  kind: 'pb' | 'hb',
  response: PBPricerResponse | HBPricerResponse | PricerErrorResponse | undefined,
  request: unknown,
  t: (key: string, params?: object) => string,
): string | null {
  const hasRequest = request != null
  const requestBlock = hasRequest
    ? `<strong>${t(`${kind}-request`)}</strong><br>` +
      `<pre>${JSON.stringify(request, null, 2)}</pre>`
    : ''

  if (!response) {
    // Request-only entry: still surface what we have so a visible trace control
    // never opens an empty dialog.
    return hasRequest ? `<strong>${t('no-pricer-response')}</strong><br><br>${requestBlock}` : null
  }

  if (isPricerError(response)) {
    return (
      `<strong>${t('pricer-error')}</strong><br>${response.error}<br><br>` +
      `<strong>Raw response</strong><br><pre>${JSON.stringify(response.raw, null, 2)}</pre><br>` +
      requestBlock
    )
  }

  if (kind === 'pb') {
    const pbResponse = response as PBPricerResponse
    return (
      `${pbResponse.trace.replace(/(?:\r\n|\r|\n)/g, '<br>')}<br><br>` +
      requestBlock
    )
  }

  const hbResponse = response as HBPricerResponse
  return (
    `${t('hb-trace-view-link', { url: hbResponse.traceURL })}<br><br>` +
    requestBlock
  )
}

/**
 * Opens the pricer-trace confirm dialog for a prebuilt `message`.
 *
 * Centralises the confirm-dialog configuration shared by the estimate-balance
 * and estimate-summary PB/HB trace buttons so the options stay in one place.
 * The confirm observable is subscribed internally; pass `destroy$` so the
 * subscription is torn down with the host component (omit it for callers
 * without a destroy lifecycle).
 *
 * @param confirmService The shared confirm dialog service.
 * @param message Prebuilt HTML message (see {@link buildPricerTraceMessage}).
 * @param destroy$ Optional teardown notifier tied to the host's lifecycle.
 */
export function openPricerTraceDialog(
  confirmService: ConfirmService,
  message: string,
  destroy$?: Observable<void>,
): void {
  const confirm$ = confirmService.confirm({
    message,
    html: true,
    hideNoButton: true,
    yesBtnLabel: 'common.close',
    width: '800px',
  })

  const piped$ = destroy$
    ? confirm$.pipe(take(1), takeUntil(destroy$))
    : confirm$.pipe(take(1))

  piped$.subscribe()
}
